import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Detail.module.scss';
import type { MilestoneRow } from '../intelligence';
import type { Tone } from '../detail';
import { formatDay } from '../views/tiles';
import DataTable from './DataTable';
import StatusBadge from './StatusBadge';
import InlineBar from './InlineBar';

/**
 * The states the milestone overview speaks. Reached means confirmed by someone;
 * ready means all its deliverables are done and it waits for that confirmation.
 */
type OverviewState = 'reached' | 'ready' | 'overdue' | 'atRisk' | 'upcoming';

const STATES: OverviewState[] = ['reached', 'ready', 'overdue', 'atRisk', 'upcoming'];

const STATE_TONE: Record<OverviewState, Tone> = {
  reached: 'good',
  ready: 'warning',
  overdue: 'critical',
  atRisk: 'serious',
  upcoming: 'neutral',
};

/**
 * The engine's milestone status in the overview's three states. A milestone
 * without assigned deliverables has no status; it reads as upcoming until its
 * deadline and is left unjudged after it.
 */
function overviewState(row: MilestoneRow, today: string): OverviewState | undefined {
  if (row.status === 'ACHIEVED') return 'reached';
  if (row.status === 'READY') return 'ready';
  if (row.status === 'OVERDUE') return 'overdue';
  if (row.status === 'AT_RISK') return 'atRisk';
  if (row.status) return 'upcoming';
  return row.deadline && row.deadline >= today ? 'upcoming' : undefined;
}

interface MilestoneOverviewProps {
  milestones: MilestoneRow[];
}

/**
 * The milestones at a glance: how many are reached, overdue and upcoming, and
 * each with its deadline and status - enough to see whether a key date is at
 * stake without going through the whole plan.
 */
const MilestoneOverview: React.FC<MilestoneOverviewProps> = ({ milestones }) => {
  const { t } = useTranslation('templates');
  const today = new Date().toISOString().slice(0, 10);
  const rows = milestones.map((m) => ({ ...m, state: overviewState(m, today) }));
  const count = (state: OverviewState) => rows.filter((r) => r.state === state).length;

  return (
    <>
      <div className={styles.milestoneSummary}>
        {STATES.map((state) => (
          <div key={state}>
            <span className={styles.tileLabel}>{t(`report.milestones.state.${state}`)}</span>
            <span className={`${styles.milestoneCount} ${(state === 'overdue' || state === 'atRisk') && count(state) > 0 ? styles.critical : ''}`}>
              {count(state)}
            </span>
          </div>
        ))}
      </div>
      <DataTable
        rows={rows}
        rowKey={(row) => row.id}
        empty={t('report.milestones.empty')}
        columns={[
          { key: 'name', header: t('report.milestones.col.name'), render: (row) => row.name },
          { key: 'deadline', header: t('report.milestones.col.deadline'), render: (row) => formatDay(row.deadline) },
          { key: 'progress', header: t('report.milestones.col.progress'), render: (row) => <InlineBar value={row.progress} /> },
          {
            key: 'status',
            header: t('report.milestones.col.status'),
            render: (row) =>
              row.state ? (
                <StatusBadge tone={STATE_TONE[row.state]} label={t(`report.milestones.state.${row.state}`)} />
              ) : (
                <span className={styles.muted} title={t('report.milestones.noDeliverablesHint')}>
                  —
                </span>
              ),
          },
        ]}
      />
    </>
  );
};

export default MilestoneOverview;
